import { useEffect, useMemo, useRef, useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import { toast } from "sonner";
import {
  LogOut,
  Plus,
  Upload,
  Trash2,
  Loader2,
  Mail,
  User,
  Building2,
  X,
  Phone,
  Globe,
  StickyNote,
  Kanban,
  Users,
  UserX,
  Send,
  Eye,
  GripVertical,
  ChevronUp,
  ChevronDown,
  Copy,
  Image as ImageIcon,
  Minus,
  List as ListIcon,
  Quote as QuoteIcon,
  Type as TypeIcon,
  MousePointerClick,
} from "lucide-react";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { adminApi, clearAdminToken } from "@/lib/adminApi";
import { LOGO_URL } from "@/lib/content";

const STAGES = [
  { id: "info_pack_requested", label: "Info Pack Requested" },
  { id: "info_pack_sent", label: "Info Pack Sent" },
  { id: "new_lead", label: "New Lead" },
  { id: "intro_email_sent", label: "Intro Email Sent" },
  { id: "responded", label: "Responded" },
  { id: "call_booked", label: "Call Booked" },
  { id: "call_done", label: "Call Done" },
  { id: "under_construction", label: "Under Construction" },
  { id: "live_client", label: "Live Client" },
];

export default function Pipeline() {
  const navigate = useNavigate();
  const [leads, setLeads] = useState([]);
  const [loading, setLoading] = useState(true);
  const [dragOverStage, setDragOverStage] = useState(null);
  const draggedIdRef = useRef(null);
  const dragStartXY = useRef(null);

  const [addOpen, setAddOpen] = useState(false);
  const [importOpen, setImportOpen] = useState(false);
  const [broadcastOpen, setBroadcastOpen] = useState(false);
  const [unsubsOpen, setUnsubsOpen] = useState(false);
  const [openLeadId, setOpenLeadId] = useState(null);

  const load = async () => {
    setLoading(true);
    try {
      const { data } = await adminApi.get("/admin/pipeline");
      setLeads(data);
    } catch {
      // 401 handled by interceptor
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    load();
  }, []);

  const logout = () => {
    clearAdminToken();
    navigate("/admin/login", { replace: true });
  };

  const byStage = useMemo(() => {
    const map = Object.fromEntries(STAGES.map((s) => [s.id, []]));
    for (const l of leads) {
      if (map[l.stage]) map[l.stage].push(l);
      else map.new_lead.push(l);
    }
    return map;
  }, [leads]);

  const openLead = useMemo(
    () => (openLeadId ? leads.find((l) => l.id === openLeadId) : null),
    [openLeadId, leads]
  );

  const moveLead = async (leadId, newStage) => {
    const prev = leads;
    setLeads((ls) => ls.map((l) => (l.id === leadId ? { ...l, stage: newStage } : l)));
    try {
      await adminApi.patch(`/admin/pipeline/${leadId}`, { stage: newStage });
    } catch (err) {
      setLeads(prev);
      const detail = err?.response?.data?.detail || "Failed to move lead.";
      toast.error(typeof detail === "string" ? detail : "Move failed.");
    }
  };

  const deleteLead = async (leadId) => {
    if (!window.confirm("Delete this lead?")) return;
    const prev = leads;
    setLeads((ls) => ls.filter((l) => l.id !== leadId));
    setOpenLeadId(null);
    try {
      await adminApi.delete(`/admin/pipeline/${leadId}`);
      toast.success("Lead deleted.");
    } catch {
      setLeads(prev);
      toast.error("Delete failed.");
    }
  };

  const patchLead = async (leadId, patch) => {
    try {
      const { data } = await adminApi.patch(`/admin/pipeline/${leadId}`, patch);
      setLeads((ls) => ls.map((l) => (l.id === leadId ? data : l)));
      return data;
    } catch (err) {
      const detail = err?.response?.data?.detail || "Failed to save.";
      toast.error(typeof detail === "string" ? detail : "Save failed.");
      throw err;
    }
  };

  const addNote = async (leadId, text) => {
    try {
      const { data } = await adminApi.post(`/admin/pipeline/${leadId}/notes`, { text });
      setLeads((ls) => ls.map((l) => (l.id === leadId ? data : l)));
      return data;
    } catch (err) {
      const detail = err?.response?.data?.detail || "Failed to add note.";
      toast.error(typeof detail === "string" ? detail : "Note failed.");
      throw err;
    }
  };

  const deleteNote = async (leadId, noteId) => {
    try {
      const { data } = await adminApi.delete(`/admin/pipeline/${leadId}/notes/${noteId}`);
      setLeads((ls) => ls.map((l) => (l.id === leadId ? data : l)));
    } catch {
      toast.error("Delete note failed.");
    }
  };

  const [sendingInfoPackId, setSendingInfoPackId] = useState(null);
  const sendInfoPack = async (leadId) => {
    if (sendingInfoPackId) return;
    setSendingInfoPackId(leadId);
    try {
      await adminApi.post(`/admin/pipeline/${leadId}/send-info-pack`);
      toast.success("Info Pack sent.");
      await load();
    } catch (err) {
      const detail = err?.response?.data?.detail || "Failed to send info pack.";
      toast.error(typeof detail === "string" ? detail : "Send failed.");
    } finally {
      setSendingInfoPackId(null);
    }
  };

  const totalLeads = leads.length;

  return (
    <div
      data-testid="admin-pipeline-page"
      className="min-h-screen bg-black text-[#F5F5F0] flex flex-col"
    >
      {/* HEADER */}
      <header className="border-b border-white/10 sticky top-0 z-30 bg-black/85 backdrop-blur-xl">
        <div className="max-w-[1600px] mx-auto px-6 md:px-8 flex items-center justify-between h-20">
          <div className="flex items-center gap-4">
            <img src={LOGO_URL} alt="Swingle" className="h-12 w-auto object-contain" />
            <nav className="hidden md:flex items-center gap-4 ml-2">
              <Link
                to="/admin"
                data-testid="pipeline-nav-pipeline"
                className="inline-flex items-center gap-2 text-xs uppercase tracking-[0.24em] text-[#D9A6F5]"
              >
                <Kanban size={14} /> Pipeline
              </Link>
              <Link
                to="/admin/venues"
                data-testid="pipeline-nav-venues"
                className="inline-flex items-center gap-2 text-xs uppercase tracking-[0.24em] text-[#A3A3A3] hover:text-[#D9A6F5] transition-colors"
              >
                <Building2 size={14} /> Venues
              </Link>
              <Link
                to="/admin/applications"
                data-testid="pipeline-nav-applications"
                className="inline-flex items-center gap-2 text-xs uppercase tracking-[0.24em] text-[#A3A3A3] hover:text-[#D9A6F5] transition-colors"
              >
                <Building2 size={14} /> Applications
              </Link>
              <Link
                to="/admin/contacts"
                data-testid="pipeline-nav-contacts"
                className="inline-flex items-center gap-2 text-xs uppercase tracking-[0.24em] text-[#A3A3A3] hover:text-[#D9A6F5] transition-colors"
              >
                <Users size={14} /> Contacts
              </Link>
            </nav>
            <div className="hidden md:block">
              <div className="text-xs uppercase tracking-[0.28em] text-[#D9A6F5]">
                Sales Pipeline
              </div>
              <div className="text-sm text-[#A3A3A3]">
                {loading ? "Loading…" : `${totalLeads} lead${totalLeads === 1 ? "" : "s"}`}
              </div>
            </div>
          </div>
          <div className="flex items-center gap-2">
            <button
              type="button"
              data-testid="pipeline-broadcast-btn"
              onClick={() => setBroadcastOpen(true)}
              className="inline-flex items-center gap-2 border border-white/20 text-[#F5F5F0] hover:border-[#D9A6F5] hover:text-[#D9A6F5] px-4 py-2.5 text-xs uppercase tracking-[0.2em] font-bold transition-colors"
            >
              <Send size={14} /> Broadcast
            </button>
            <button
              type="button"
              data-testid="pipeline-unsubs-btn"
              onClick={() => setUnsubsOpen(true)}
              className="inline-flex items-center gap-2 border border-white/20 text-[#F5F5F0] hover:border-[#D9A6F5] hover:text-[#D9A6F5] px-4 py-2.5 text-xs uppercase tracking-[0.2em] font-bold transition-colors"
            >
              <UserX size={14} /> Unsubs
            </button>
            <button
              type="button"
              data-testid="pipeline-import-btn"
              onClick={() => setImportOpen(true)}
              className="inline-flex items-center gap-2 border border-white/20 text-[#F5F5F0] hover:border-[#D9A6F5] hover:text-[#D9A6F5] px-4 py-2.5 text-xs uppercase tracking-[0.2em] font-bold transition-colors"
            >
              <Upload size={14} /> Import CSV
            </button>
            <button
              type="button"
              data-testid="pipeline-add-btn"
              onClick={() => setAddOpen(true)}
              className="inline-flex items-center gap-2 bg-[#D9A6F5] text-black hover:bg-[#E8C8FF] px-4 py-2.5 text-xs uppercase tracking-[0.2em] font-bold transition-colors"
            >
              <Plus size={14} /> Add Lead
            </button>
            <button
              type="button"
              data-testid="pipeline-logout-btn"
              onClick={logout}
              className="ml-2 inline-flex items-center gap-2 text-[#A3A3A3] hover:text-[#D9A6F5] px-3 py-2 text-xs uppercase tracking-[0.2em] transition-colors"
            >
              <LogOut size={14} /> Sign out
            </button>
          </div>
        </div>
      </header>

      {/* KANBAN */}
      <div className="flex-1 overflow-x-auto">
        <div className="min-w-[1800px] max-w-[2000px] mx-auto px-6 md:px-8 py-6">
          {loading ? (
            <div className="h-[60vh] flex items-center justify-center">
              <Loader2 size={20} className="animate-spin text-[#D9A6F5]" />
            </div>
          ) : (
            <div className="grid grid-cols-9 gap-4">
              {STAGES.map((stage) => (
                <div
                  key={stage.id}
                  data-testid={`pipeline-column-${stage.id}`}
                  onDragOver={(e) => {
                    e.preventDefault();
                    setDragOverStage(stage.id);
                  }}
                  onDragLeave={() => setDragOverStage(null)}
                  onDrop={(e) => {
                    e.preventDefault();
                    const id = draggedIdRef.current || e.dataTransfer.getData("text/plain");
                    setDragOverStage(null);
                    draggedIdRef.current = null;
                    if (!id) return;
                    const lead = leads.find((l) => l.id === id);
                    if (!lead || lead.stage === stage.id) return;
                    moveLead(id, stage.id);
                  }}
                  className={`flex flex-col bg-[#0A0A0A] border transition-colors ${
                    dragOverStage === stage.id ? "border-[#D9A6F5]" : "border-white/10"
                  }`}
                >
                  <div className="px-4 py-3 border-b border-white/10 flex items-center justify-between">
                    <div className="text-[11px] uppercase tracking-[0.22em] text-[#D9A6F5] font-semibold">
                      {stage.label}
                    </div>
                    <div className="text-xs text-[#A3A3A3] font-mono">
                      {byStage[stage.id].length}
                    </div>
                  </div>
                  <div className="flex-1 p-3 space-y-3 min-h-[60vh]">
                    {byStage[stage.id].length === 0 && (
                      <div className="text-xs text-[#A3A3A3]/50 uppercase tracking-[0.2em] text-center py-8">
                        Drop leads here
                      </div>
                    )}
                    {byStage[stage.id].map((lead) => (
                      <LeadCard
                        key={lead.id}
                        lead={lead}
                        sendingInfoPack={sendingInfoPackId === lead.id}
                        onSendInfoPack={() => sendInfoPack(lead.id)}
                        onDragStart={(e) => {
                          draggedIdRef.current = lead.id;
                          dragStartXY.current = { x: e.clientX, y: e.clientY };
                        }}
                        onDragEnd={() => {
                          draggedIdRef.current = null;
                          setDragOverStage(null);
                          dragStartXY.current = null;
                        }}
                        onClick={() => setOpenLeadId(lead.id)}
                      />
                    ))}
                  </div>
                </div>
              ))}
            </div>
          )}
        </div>
      </div>

      {addOpen && <AddLeadModal onClose={() => setAddOpen(false)} onSaved={load} />}
      {importOpen && <ImportModal onClose={() => setImportOpen(false)} onImported={load} />}
      {broadcastOpen && (
        <BroadcastModal onClose={() => setBroadcastOpen(false)} />
      )}
      {unsubsOpen && <UnsubscribesModal onClose={() => setUnsubsOpen(false)} />}
      {openLead && (
        <LeadDetailModal
          lead={openLead}
          onClose={() => setOpenLeadId(null)}
          onPatch={(patch) => patchLead(openLead.id, patch)}
          onAddNote={(text) => addNote(openLead.id, text)}
          onDeleteNote={(noteId) => deleteNote(openLead.id, noteId)}
          onDelete={() => deleteLead(openLead.id)}
        />
      )}
    </div>
  );
}

function LeadCard({ lead, onDragStart, onDragEnd, onClick, sendingInfoPack, onSendInfoPack }) {
  const dragged = useRef(false);
  const isInfoPackPending = lead.stage === "info_pack_requested";
  return (
    <div
      draggable
      data-testid={`lead-card-${lead.id}`}
      role="button"
      tabIndex={0}
      onDragStart={(e) => {
        dragged.current = true;
        e.dataTransfer.setData("text/plain", lead.id);
        e.dataTransfer.effectAllowed = "move";
        onDragStart?.(e);
      }}
      onDragEnd={(e) => {
        // Slight delay so the following click event (some browsers fire click after drag) sees dragged=true
        setTimeout(() => (dragged.current = false), 50);
        onDragEnd?.(e);
      }}
      onClick={() => {
        if (dragged.current) return;
        onClick?.();
      }}
      onKeyDown={(e) => {
        if (e.key === "Enter" || e.key === " ") {
          e.preventDefault();
          onClick?.();
        }
      }}
      className="group bg-black border border-white/10 hover:border-[#D9A6F5]/60 focus:border-[#D9A6F5] cursor-pointer p-3 transition-colors outline-none"
    >
      <div className="flex items-start justify-between gap-2">
        <div className="min-w-0 flex-1">
          <div className="flex items-center gap-1.5 text-[#F5F5F0] font-semibold text-sm truncate">
            <Building2 size={12} className="text-[#D9A6F5] shrink-0" />
            <span className="truncate">{lead.venue}</span>
          </div>
          {lead.name && (
            <div className="mt-1.5 flex items-center gap-1.5 text-xs text-[#A3A3A3] truncate">
              <User size={11} className="shrink-0" /> <span className="truncate">{lead.name}</span>
            </div>
          )}
          {lead.email && (
            <div className="mt-1 flex items-center gap-1.5 text-xs text-[#A3A3A3] truncate">
              <Mail size={11} className="shrink-0" />
              <span className="truncate">{lead.email}</span>
            </div>
          )}
        </div>
        {lead.notes?.length > 0 && (
          <div
            className="inline-flex items-center gap-1 text-[10px] text-[#D9A6F5]/80"
            aria-label={`${lead.notes.length} notes`}
            title={`${lead.notes.length} notes`}
          >
            <StickyNote size={11} />
            {lead.notes.length}
          </div>
        )}
      </div>
      {lead.source && lead.source !== "manual" && (
        <div className="mt-2 inline-block text-[9px] uppercase tracking-[0.2em] text-[#D9A6F5]/70 border border-[#D9A6F5]/30 px-1.5 py-0.5">
          {lead.source === "csv"
            ? "CSV"
            : lead.source === "contact_form"
            ? "Web form"
            : lead.source === "info_pack"
            ? "Info Pack"
            : lead.source}
        </div>
      )}
      {isInfoPackPending && (
        <button
          type="button"
          data-testid={`lead-card-send-info-pack-${lead.id}`}
          onClick={(e) => {
            e.stopPropagation();
            if (!sendingInfoPack) onSendInfoPack?.();
          }}
          disabled={sendingInfoPack || !lead.email}
          title={!lead.email ? "Lead has no email address" : "Approve & send Info Pack"}
          className="mt-3 w-full inline-flex items-center justify-center gap-2 bg-[#D9A6F5] text-black hover:bg-[#E8C8FF] disabled:opacity-50 disabled:cursor-not-allowed px-3 py-2 text-[10px] uppercase tracking-[0.22em] font-bold transition-colors"
        >
          {sendingInfoPack ? (
            <>
              <Loader2 size={11} className="animate-spin" /> Sending…
            </>
          ) : (
            <>
              <Mail size={11} /> Approve &amp; Send
            </>
          )}
        </button>
      )}
    </div>
  );
}

function AddLeadModal({ onClose, onSaved }) {
  const [venue, setVenue] = useState("");
  const [name, setName] = useState("");
  const [email, setEmail] = useState("");
  const [phone, setPhone] = useState("");
  const [website, setWebsite] = useState("");
  const [stage, setStage] = useState("new_lead");
  const [saving, setSaving] = useState(false);

  const submit = async (e) => {
    e.preventDefault();
    if (!venue || saving) return;
    setSaving(true);
    try {
      await adminApi.post("/admin/pipeline", {
        venue: venue.trim(),
        name: name.trim() || null,
        email: email.trim() || null,
        phone: phone.trim() || null,
        website: website.trim() || null,
        stage,
      });
      toast.success("Lead added.");
      onSaved?.();
      onClose();
    } catch (err) {
      const detail = err?.response?.data?.detail || "Failed to add lead.";
      toast.error(typeof detail === "string" ? detail : "Add failed.");
    } finally {
      setSaving(false);
    }
  };

  return (
    <ModalShell onClose={onClose} title="Add Lead" testId="add-lead-modal">
      <form onSubmit={submit} className="space-y-5" data-testid="add-lead-form" noValidate>
        <Field label="Venue name" required>
          <Input
            data-testid="add-lead-venue"
            value={venue}
            onChange={(e) => setVenue(e.target.value)}
            placeholder="The Basement, Bristol"
            required
            autoFocus
            className={inputCls}
          />
        </Field>
        <div className="grid grid-cols-2 gap-4">
          <Field label="Contact name">
            <Input
              data-testid="add-lead-name"
              value={name}
              onChange={(e) => setName(e.target.value)}
              placeholder="Alex Bennett"
              className={inputCls}
            />
          </Field>
          <Field label="Email">
            <Input
              data-testid="add-lead-email"
              type="email"
              value={email}
              onChange={(e) => setEmail(e.target.value)}
              placeholder="alex@yourvenue.co.uk"
              className={inputCls}
            />
          </Field>
        </div>
        <div className="grid grid-cols-2 gap-4">
          <Field label="Phone">
            <Input
              data-testid="add-lead-phone"
              type="tel"
              value={phone}
              onChange={(e) => setPhone(e.target.value)}
              placeholder="07000 000 000"
              className={inputCls}
            />
          </Field>
          <Field label="Website">
            <Input
              data-testid="add-lead-website"
              type="url"
              value={website}
              onChange={(e) => setWebsite(e.target.value)}
              placeholder="https://…"
              className={inputCls}
            />
          </Field>
        </div>
        <Field label="Starting stage">
          <select
            data-testid="add-lead-stage"
            value={stage}
            onChange={(e) => setStage(e.target.value)}
            className={`${inputCls} pr-3`}
          >
            {STAGES.map((s) => (
              <option key={s.id} value={s.id} className="bg-black">
                {s.label}
              </option>
            ))}
          </select>
        </Field>
        <button
          type="submit"
          disabled={saving || !venue}
          data-testid="add-lead-submit"
          className="w-full inline-flex items-center justify-center gap-2 bg-[#D9A6F5] text-black hover:bg-[#E8C8FF] disabled:opacity-60 px-6 py-3.5 text-xs uppercase tracking-[0.22em] font-bold transition-colors"
        >
          {saving ? <Loader2 size={14} className="animate-spin" /> : null}
          Add To Pipeline
        </button>
      </form>
    </ModalShell>
  );
}

function LeadDetailModal({ lead, onClose, onPatch, onAddNote, onDeleteNote, onDelete }) {
  const [venue, setVenue] = useState(lead.venue || "");
  const [name, setName] = useState(lead.name || "");
  const [email, setEmail] = useState(lead.email || "");
  const [phone, setPhone] = useState(lead.phone || "");
  const [website, setWebsite] = useState(lead.website || "");
  const [stage, setStage] = useState(lead.stage);
  const [saving, setSaving] = useState(false);
  const [noteText, setNoteText] = useState("");
  const [addingNote, setAddingNote] = useState(false);

  const dirty =
    venue !== (lead.venue || "") ||
    name !== (lead.name || "") ||
    email !== (lead.email || "") ||
    phone !== (lead.phone || "") ||
    website !== (lead.website || "") ||
    stage !== lead.stage;

  const save = async (e) => {
    e?.preventDefault();
    if (!dirty || saving || !venue.trim()) return;
    setSaving(true);
    try {
      await onPatch({
        venue: venue.trim(),
        name: name.trim() || null,
        email: email.trim() || null,
        phone: phone.trim() || null,
        website: website.trim() || null,
        stage,
      });
      toast.success("Lead saved.");
    } catch {
      /* toast handled in caller */
    } finally {
      setSaving(false);
    }
  };

  const submitNote = async (e) => {
    e.preventDefault();
    const text = noteText.trim();
    if (!text || addingNote) return;
    setAddingNote(true);
    try {
      await onAddNote(text);
      setNoteText("");
    } catch {
      /* handled */
    } finally {
      setAddingNote(false);
    }
  };

  const formatDate = (d) => {
    if (!d) return "";
    try {
      const date = new Date(d);
      return date.toLocaleString("en-GB", {
        day: "numeric",
        month: "short",
        year: "numeric",
        hour: "2-digit",
        minute: "2-digit",
      });
    } catch {
      return String(d);
    }
  };

  return (
    <ModalShell onClose={onClose} title={`Edit — ${lead.venue}`} testId="lead-detail-modal" size="lg">
      <form onSubmit={save} className="space-y-5" data-testid="lead-detail-form" noValidate>
        <Field label="Venue name" required>
          <Input
            data-testid="lead-detail-venue"
            value={venue}
            onChange={(e) => setVenue(e.target.value)}
            required
            className={inputCls}
          />
        </Field>
        <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
          <Field label="Contact name">
            <Input
              data-testid="lead-detail-name"
              value={name}
              onChange={(e) => setName(e.target.value)}
              placeholder="Alex Bennett"
              className={inputCls}
            />
          </Field>
          <Field label="Email">
            <Input
              data-testid="lead-detail-email"
              type="email"
              value={email}
              onChange={(e) => setEmail(e.target.value)}
              placeholder="alex@yourvenue.co.uk"
              className={inputCls}
            />
          </Field>
        </div>
        <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
          <Field label="Phone">
            <Input
              data-testid="lead-detail-phone"
              type="tel"
              value={phone}
              onChange={(e) => setPhone(e.target.value)}
              placeholder="07000 000 000"
              className={inputCls}
            />
          </Field>
          <Field label="Website">
            <Input
              data-testid="lead-detail-website"
              type="url"
              value={website}
              onChange={(e) => setWebsite(e.target.value)}
              placeholder="https://…"
              className={inputCls}
            />
          </Field>
        </div>
        <Field label="Stage">
          <select
            data-testid="lead-detail-stage"
            value={stage}
            onChange={(e) => setStage(e.target.value)}
            className={`${inputCls} pr-3`}
          >
            {STAGES.map((s) => (
              <option key={s.id} value={s.id} className="bg-black">
                {s.label}
              </option>
            ))}
          </select>
        </Field>

        <div className="flex items-center gap-3 pt-2">
          <button
            type="submit"
            disabled={saving || !dirty || !venue.trim()}
            data-testid="lead-detail-save"
            className="flex-1 inline-flex items-center justify-center gap-2 bg-[#D9A6F5] text-black hover:bg-[#E8C8FF] disabled:opacity-40 disabled:cursor-not-allowed px-6 py-3 text-xs uppercase tracking-[0.22em] font-bold transition-colors"
          >
            {saving ? <Loader2 size={14} className="animate-spin" /> : null}
            {dirty ? "Save Changes" : "Saved"}
          </button>
          <button
            type="button"
            data-testid="lead-detail-delete"
            onClick={onDelete}
            className="inline-flex items-center gap-2 border border-red-500/40 text-red-400 hover:bg-red-500/10 px-4 py-3 text-xs uppercase tracking-[0.22em] font-bold transition-colors"
          >
            <Trash2 size={14} /> Delete
          </button>
        </div>
      </form>

      {/* NOTES */}
      <div className="mt-8 border-t border-white/10 pt-6">
        <div className="flex items-center gap-2 text-xs uppercase tracking-[0.22em] text-[#D9A6F5] mb-4">
          <StickyNote size={14} /> Notes ({lead.notes?.length || 0})
        </div>

        <form onSubmit={submitNote} className="mb-6 space-y-3" data-testid="lead-detail-note-form">
          <Textarea
            data-testid="lead-detail-note-input"
            value={noteText}
            onChange={(e) => setNoteText(e.target.value)}
            rows={3}
            placeholder="Add a note — a call summary, a follow-up reminder, anything…"
            className={`${inputCls} h-auto`}
          />
          <button
            type="submit"
            disabled={!noteText.trim() || addingNote}
            data-testid="lead-detail-note-submit"
            className="inline-flex items-center gap-2 border border-[#D9A6F5] text-[#D9A6F5] hover:bg-[#D9A6F5] hover:text-black disabled:opacity-40 disabled:cursor-not-allowed px-5 py-2.5 text-xs uppercase tracking-[0.22em] font-bold transition-colors"
          >
            {addingNote ? <Loader2 size={12} className="animate-spin" /> : <Plus size={12} />}
            Add Note
          </button>
        </form>

        <div className="space-y-3 max-h-[40vh] overflow-y-auto pr-1">
          {(lead.notes?.length || 0) === 0 && (
            <div className="text-sm text-[#A3A3A3]/60 uppercase tracking-[0.2em] text-center py-6">
              No notes yet
            </div>
          )}
          {[...(lead.notes || [])]
            .sort((a, b) => new Date(b.created_at) - new Date(a.created_at))
            .map((n) => (
              <div
                key={n.id}
                data-testid={`lead-detail-note-${n.id}`}
                className="group bg-black border border-white/10 p-4"
              >
                <div className="flex items-start justify-between gap-3">
                  <div className="text-sm text-[#F5F5F0] whitespace-pre-wrap leading-relaxed flex-1">
                    {n.text}
                  </div>
                  <button
                    type="button"
                    onClick={() => onDeleteNote(n.id)}
                    className="opacity-0 group-hover:opacity-100 text-[#A3A3A3] hover:text-red-400 transition-opacity p-1"
                    data-testid={`lead-detail-note-delete-${n.id}`}
                    aria-label="Delete note"
                  >
                    <Trash2 size={12} />
                  </button>
                </div>
                <div className="mt-2 text-[10px] uppercase tracking-[0.22em] text-[#A3A3A3]">
                  {formatDate(n.created_at)}
                </div>
              </div>
            ))}
        </div>
      </div>
    </ModalShell>
  );
}

function ImportModal({ onClose, onImported }) {
  const [file, setFile] = useState(null);
  const [uploading, setUploading] = useState(false);
  const [result, setResult] = useState(null);

  const submit = async (e) => {
    e.preventDefault();
    if (!file || uploading) return;
    setUploading(true);
    setResult(null);
    try {
      const fd = new FormData();
      fd.append("file", file);
      const { data } = await adminApi.post("/admin/pipeline/import", fd, {
        headers: { "Content-Type": "multipart/form-data" },
      });
      setResult(data);
      toast.success(`Imported ${data.inserted} lead${data.inserted === 1 ? "" : "s"}.`);
      onImported?.();
    } catch (err) {
      const detail = err?.response?.data?.detail || "Import failed.";
      toast.error(typeof detail === "string" ? detail : "Import failed.");
    } finally {
      setUploading(false);
    }
  };

  return (
    <ModalShell onClose={onClose} title="Import CSV" testId="import-csv-modal">
      <form onSubmit={submit} className="space-y-5" data-testid="import-csv-form" noValidate>
        <p className="text-sm text-[#A3A3A3] leading-relaxed">
          CSV with headers. Accepted columns (any order, case-insensitive):{" "}
          <span className="text-[#F5F5F0]">venue</span>,{" "}
          <span className="text-[#F5F5F0]">name</span>,{" "}
          <span className="text-[#F5F5F0]">email</span>,{" "}
          <span className="text-[#F5F5F0]">phone</span>,{" "}
          <span className="text-[#F5F5F0]">website</span>. All imported leads land in{" "}
          <span className="text-[#D9A6F5]">New Lead</span>.
        </p>
        <label
          htmlFor="csv"
          className="flex flex-col items-center justify-center gap-3 border-2 border-dashed border-white/15 hover:border-[#D9A6F5]/60 p-8 cursor-pointer transition-colors"
        >
          <Upload size={22} className="text-[#D9A6F5]" />
          <div className="text-sm text-[#F5F5F0]">
            {file ? file.name : "Click to select a .csv file"}
          </div>
          <input
            id="csv"
            type="file"
            accept=".csv,text/csv"
            data-testid="import-csv-file"
            onChange={(e) => setFile(e.target.files?.[0] || null)}
            className="hidden"
          />
        </label>
        {result && (
          <div
            data-testid="import-csv-result"
            className="text-sm text-[#A3A3A3] bg-black border border-white/10 p-4"
          >
            <div>
              <span className="text-[#D9A6F5]">Imported:</span>{" "}
              <span className="text-[#F5F5F0] font-semibold">{result.inserted}</span>
            </div>
            {result.skipped > 0 && (
              <div className="mt-1">
                <span className="text-[#D9A6F5]">Skipped:</span>{" "}
                <span className="text-[#F5F5F0]">{result.skipped}</span>
              </div>
            )}
            {result.errors?.length > 0 && (
              <ul className="mt-2 text-xs text-[#A3A3A3] list-disc list-inside space-y-0.5">
                {result.errors.slice(0, 5).map((er, i) => (
                  <li key={i}>{er}</li>
                ))}
              </ul>
            )}
          </div>
        )}
        <button
          type="submit"
          disabled={!file || uploading}
          data-testid="import-csv-submit"
          className="w-full inline-flex items-center justify-center gap-2 bg-[#D9A6F5] text-black hover:bg-[#E8C8FF] disabled:opacity-60 px-6 py-3.5 text-xs uppercase tracking-[0.22em] font-bold transition-colors"
        >
          {uploading ? <Loader2 size={14} className="animate-spin" /> : <Upload size={14} />}
          {uploading ? "Uploading…" : "Upload CSV"}
        </button>
      </form>
    </ModalShell>
  );
}

// ---- Block-based email composer -------------------------------------------------

const emptyBlock = (type) => {
  const id = Math.random().toString(36).slice(2, 10);
  switch (type) {
    case "text":
      return { id, type, content: "" };
    case "cta":
      return { id, type, label: "Click here", url: "https://", align: "center" };
    case "image":
      return { id, type, url: "", alt: "", align: "center", width_pct: 100 };
    case "divider":
      return { id, type };
    case "list":
      return { id, type, items: [""] };
    case "quote":
      return { id, type, content: "", cite: "" };
    default:
      return { id, type: "text", content: "" };
  }
};

const BLOCK_MENU = [
  { type: "text", label: "Text", icon: TypeIcon },
  { type: "cta", label: "CTA Button", icon: MousePointerClick },
  { type: "image", label: "Image", icon: ImageIcon },
  { type: "divider", label: "Divider", icon: Minus },
  { type: "list", label: "Bullet list", icon: ListIcon },
  { type: "quote", label: "Quote", icon: QuoteIcon },
];

function BroadcastModal({ onClose }) {
  const [fromName, setFromName] = useState("Swingle For Venues");
  const [subject, setSubject] = useState("");
  const [blocks, setBlocks] = useState([emptyBlock("text")]);
  const [stage, setStage] = useState("new_lead");
  const [testEmail, setTestEmail] = useState("");
  const [count, setCount] = useState(null);
  const [sendingTest, setSendingTest] = useState(false);
  const [sendingReal, setSendingReal] = useState(false);
  const [result, setResult] = useState(null);
  const [showPreview, setShowPreview] = useState(true);

  useEffect(() => {
    let alive = true;
    (async () => {
      try {
        const { data } = await adminApi.get(`/admin/broadcast/count?stage=${stage}`);
        if (alive) setCount(data);
      } catch {
        /* ignore */
      }
    })();
    return () => {
      alive = false;
    };
  }, [stage]);

  // Block operations
  const updateBlock = (id, patch) =>
    setBlocks((bs) => bs.map((b) => (b.id === id ? { ...b, ...patch } : b)));
  const removeBlock = (id) =>
    setBlocks((bs) => (bs.length === 1 ? bs : bs.filter((b) => b.id !== id)));
  const duplicateBlock = (id) =>
    setBlocks((bs) => {
      const idx = bs.findIndex((b) => b.id === id);
      if (idx < 0) return bs;
      const copy = { ...bs[idx], id: Math.random().toString(36).slice(2, 10) };
      return [...bs.slice(0, idx + 1), copy, ...bs.slice(idx + 1)];
    });
  const moveBlock = (id, dir) =>
    setBlocks((bs) => {
      const idx = bs.findIndex((b) => b.id === id);
      const newIdx = idx + dir;
      if (idx < 0 || newIdx < 0 || newIdx >= bs.length) return bs;
      const next = [...bs];
      [next[idx], next[newIdx]] = [next[newIdx], next[idx]];
      return next;
    });
  const addBlock = (type) =>
    setBlocks((bs) => [...bs, emptyBlock(type)]);

  const payloadBase = () => ({
    subject: subject.trim(),
    from_name: fromName.trim() || "Swingle For Venues",
    blocks: blocks.map(({ id, ...rest }) => {
      void id;
      // For list blocks: strip empty items
      if (rest.type === "list") {
        return { ...rest, items: (rest.items || []).map((s) => s.trim()).filter(Boolean) };
      }
      return rest;
    }),
  });

  const sendTest = async () => {
    const email = testEmail.trim();
    if (!email) {
      toast.error("Enter a test email address first.");
      return;
    }
    if (!subject.trim()) {
      toast.error("Subject is required.");
      return;
    }
    setSendingTest(true);
    try {
      const { data } = await adminApi.post("/admin/broadcast", {
        ...payloadBase(),
        mode: "test",
        test_email: email,
      });
      toast.success(data.message || "Test email sent.");
    } catch (err) {
      const detail = err?.response?.data?.detail || "Test send failed.";
      toast.error(typeof detail === "string" ? detail : "Test send failed.");
    } finally {
      setSendingTest(false);
    }
  };

  const sendReal = async () => {
    if (!subject.trim()) {
      toast.error("Subject is required.");
      return;
    }
    const sendable = count?.sendable ?? 0;
    if (sendable === 0) {
      toast.error("No sendable recipients in this stage.");
      return;
    }
    const label = STAGES.find((s) => s.id === stage)?.label || stage;
    if (
      !window.confirm(
        `Send this broadcast to ${sendable} lead${
          sendable === 1 ? "" : "s"
        } in "${label}"?\n\nThis will send real emails and cannot be undone.`
      )
    )
      return;
    setSendingReal(true);
    setResult(null);
    try {
      const { data } = await adminApi.post("/admin/broadcast", {
        ...payloadBase(),
        mode: "send",
        stage,
      });
      setResult(data);
      toast.success(data.message || "Broadcast complete.");
    } catch (err) {
      const detail = err?.response?.data?.detail || "Broadcast failed.";
      toast.error(typeof detail === "string" ? detail : "Broadcast failed.");
    } finally {
      setSendingReal(false);
    }
  };

  const busy = sendingTest || sendingReal;

  return (
    <ModalShell onClose={onClose} title="Send Broadcast" testId="broadcast-modal" size="xl">
      <div className="space-y-5" data-testid="broadcast-form">
        <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
          <Field label="From (display name)">
            <Input
              data-testid="broadcast-from"
              value={fromName}
              onChange={(e) => setFromName(e.target.value)}
              className={inputCls}
            />
          </Field>
          <Field label="Recipient stage">
            <select
              data-testid="broadcast-stage"
              value={stage}
              onChange={(e) => setStage(e.target.value)}
              className={`${inputCls} pr-3`}
            >
              {STAGES.map((s) => (
                <option key={s.id} value={s.id} className="bg-black">
                  {s.label}
                </option>
              ))}
            </select>
          </Field>
        </div>
        <Field label="Subject" required>
          <Input
            data-testid="broadcast-subject"
            value={subject}
            onChange={(e) => setSubject(e.target.value)}
            placeholder="What lands in the inbox subject line"
            className={inputCls}
          />
        </Field>
        <div
          data-testid="broadcast-recipient-count"
          className="text-xs text-[#A3A3A3]"
        >
          {count === null ? (
            "Loading recipient count…"
          ) : (
            <>
              <span className="text-[#D9A6F5] font-semibold">
                {count.sendable}
              </span>{" "}
              sendable · {count.with_email} with email · {count.unsubscribed}{" "}
              unsubscribed · {count.total_leads} total
            </>
          )}
        </div>

        {/* Block editor */}
        <div className="space-y-3">
          <div className="flex items-center justify-between">
            <div className="text-[11px] uppercase tracking-[0.22em] text-[#D9A6F5]">
              Email content ({blocks.length} block{blocks.length === 1 ? "" : "s"})
            </div>
            <div className="text-[10px] text-[#A3A3A3]/70">
              Markdown in text/list/quote: <span className="text-[#F5F5F0]">**bold**</span>, <span className="text-[#F5F5F0]">_italic_</span>, <span className="text-[#F5F5F0]">[link](url)</span>, <span className="text-[#F5F5F0]">[[BADGE|purple]]</span>
            </div>
          </div>
          {blocks.map((block, idx) => (
            <BlockEditor
              key={block.id}
              block={block}
              index={idx}
              total={blocks.length}
              onChange={(patch) => updateBlock(block.id, patch)}
              onMoveUp={() => moveBlock(block.id, -1)}
              onMoveDown={() => moveBlock(block.id, 1)}
              onDuplicate={() => duplicateBlock(block.id)}
              onRemove={() => removeBlock(block.id)}
            />
          ))}
          <div className="flex flex-wrap gap-2 pt-1">
            {BLOCK_MENU.map(({ type, label, icon: Icon }) => (
              <button
                key={type}
                type="button"
                data-testid={`broadcast-add-block-${type}`}
                onClick={() => addBlock(type)}
                className="inline-flex items-center gap-1.5 border border-white/15 hover:border-[#D9A6F5] hover:text-[#D9A6F5] text-[#F5F5F0] px-3 py-2 text-[10px] uppercase tracking-[0.2em] font-bold transition-colors"
              >
                <Icon size={12} /> {label}
              </button>
            ))}
          </div>
        </div>

        {/* Preview panel */}
        <div className="border border-white/10 bg-black">
          <button
            type="button"
            data-testid="broadcast-toggle-preview"
            onClick={() => setShowPreview((s) => !s)}
            className="w-full flex items-center justify-between px-4 py-3 text-xs uppercase tracking-[0.22em] text-[#A3A3A3] hover:text-[#D9A6F5] transition-colors"
          >
            <span className="inline-flex items-center gap-2">
              <Eye size={12} /> Preview
            </span>
            <span className="text-[10px]">{showPreview ? "Hide" : "Show"}</span>
          </button>
          {showPreview && (
            <div
              data-testid="broadcast-preview"
              className="border-t border-white/10 p-6 bg-black"
              style={{ fontFamily: "Arial, sans-serif" }}
            >
              <div
                style={{
                  maxWidth: 600,
                  margin: "0 auto",
                  background: "#000",
                  padding: "24px 16px",
                }}
              >
                <div style={{ textAlign: "center", marginBottom: 24 }}>
                  <img
                    src="/logo.png"
                    alt="Swingle"
                    style={{ height: 56, width: "auto", objectFit: "contain" }}
                  />
                </div>
                <div
                  style={{
                    color: "#A3A3A3",
                    fontSize: 12,
                    letterSpacing: "0.18em",
                    textTransform: "uppercase",
                    marginBottom: 12,
                  }}
                >
                  {subject || "(no subject)"}
                </div>
                {blocks.map((b) => (
                  <BlockPreview key={b.id} block={b} />
                ))}
                <p
                  style={{
                    color: "#A3A3A3",
                    lineHeight: 1.6,
                    margin: "28px 0 0 0",
                    fontSize: 13,
                  }}
                >
                  Any questions, just hit reply — we&apos;ll pick it up
                  personally.
                </p>
                <p
                  style={{
                    color: "#666",
                    fontSize: 11,
                    lineHeight: 1.6,
                    marginTop: 24,
                  }}
                >
                  — The Swingle team
                </p>
              </div>
              <div
                style={{
                  background: "#0A0A0A",
                  padding: "16px",
                  textAlign: "center",
                  color: "#A3A3A3",
                  fontSize: 11,
                  marginTop: 8,
                }}
              >
                Unsubscribe · Swingle For Venues, London, United Kingdom
              </div>
            </div>
          )}
        </div>

        {/* Test send */}
        <div className="border border-white/10 bg-black p-4">
          <div className="text-[11px] uppercase tracking-[0.22em] text-[#D9A6F5] mb-3">
            Step 1 — Send test to yourself
          </div>
          <div className="flex flex-col sm:flex-row gap-3">
            <Input
              data-testid="broadcast-test-email"
              type="email"
              value={testEmail}
              onChange={(e) => setTestEmail(e.target.value)}
              placeholder="dj@swingleapp.com"
              className={inputCls}
            />
            <button
              type="button"
              data-testid="broadcast-send-test"
              onClick={sendTest}
              disabled={busy || !testEmail.trim()}
              className="inline-flex items-center justify-center gap-2 border border-[#D9A6F5] text-[#D9A6F5] hover:bg-[#D9A6F5] hover:text-black disabled:opacity-40 disabled:cursor-not-allowed px-5 py-3 text-xs uppercase tracking-[0.22em] font-bold transition-colors whitespace-nowrap"
            >
              {sendingTest ? (
                <Loader2 size={13} className="animate-spin" />
              ) : (
                <Mail size={13} />
              )}
              Send Test
            </button>
          </div>
        </div>

        {/* Real send */}
        <div className="border border-white/10 bg-black p-4">
          <div className="text-[11px] uppercase tracking-[0.22em] text-[#D9A6F5] mb-3">
            Step 2 — Approve &amp; broadcast
          </div>
          <button
            type="button"
            data-testid="broadcast-send-all"
            onClick={sendReal}
            disabled={busy || !count || count.sendable === 0}
            className="w-full inline-flex items-center justify-center gap-2 bg-[#D9A6F5] text-black hover:bg-[#E8C8FF] disabled:opacity-40 disabled:cursor-not-allowed px-6 py-3.5 text-xs uppercase tracking-[0.22em] font-bold transition-colors"
          >
            {sendingReal ? (
              <Loader2 size={14} className="animate-spin" />
            ) : (
              <Send size={14} />
            )}
            {sendingReal
              ? "Sending…"
              : `Approve & Send To ${count?.sendable ?? "…"} Lead${
                  count?.sendable === 1 ? "" : "s"
                }`}
          </button>
        </div>

        {result && (
          <div
            data-testid="broadcast-result"
            className="border border-[#D9A6F5]/40 bg-black p-4 text-sm text-[#F5F5F0]"
          >
            <div className="text-[#D9A6F5] uppercase tracking-[0.2em] text-xs mb-2">
              Broadcast Result
            </div>
            <div>Sent: {result.sent}</div>
            <div>Skipped (no email): {result.skipped_no_email}</div>
            <div>Skipped (unsubscribed): {result.skipped_unsubscribed}</div>
            <div>Failed: {result.failed}</div>
          </div>
        )}
      </div>
    </ModalShell>
  );
}


function BlockEditor({ block, index, total, onChange, onMoveUp, onMoveDown, onDuplicate, onRemove }) {
  const labelFor = (t) => BLOCK_MENU.find((b) => b.type === t)?.label || t;
  return (
    <div
      data-testid={`broadcast-block-${index}`}
      className="border border-white/10 bg-black"
    >
      <div className="flex items-center justify-between px-3 py-2 border-b border-white/10 bg-[#0A0A0A]">
        <div className="inline-flex items-center gap-2 text-[10px] uppercase tracking-[0.22em] text-[#D9A6F5]">
          <GripVertical size={12} className="text-[#A3A3A3]" />
          #{index + 1} {labelFor(block.type)}
        </div>
        <div className="flex items-center gap-1">
          <button
            type="button"
            aria-label="Move up"
            data-testid={`broadcast-block-up-${index}`}
            onClick={onMoveUp}
            disabled={index === 0}
            className="p-1.5 text-[#A3A3A3] hover:text-[#D9A6F5] disabled:opacity-30 disabled:cursor-not-allowed"
          >
            <ChevronUp size={14} />
          </button>
          <button
            type="button"
            aria-label="Move down"
            data-testid={`broadcast-block-down-${index}`}
            onClick={onMoveDown}
            disabled={index === total - 1}
            className="p-1.5 text-[#A3A3A3] hover:text-[#D9A6F5] disabled:opacity-30 disabled:cursor-not-allowed"
          >
            <ChevronDown size={14} />
          </button>
          <button
            type="button"
            aria-label="Duplicate"
            data-testid={`broadcast-block-dup-${index}`}
            onClick={onDuplicate}
            className="p-1.5 text-[#A3A3A3] hover:text-[#D9A6F5]"
          >
            <Copy size={14} />
          </button>
          <button
            type="button"
            aria-label="Remove"
            data-testid={`broadcast-block-remove-${index}`}
            onClick={onRemove}
            disabled={total === 1}
            className="p-1.5 text-[#A3A3A3] hover:text-red-400 disabled:opacity-30 disabled:cursor-not-allowed"
          >
            <Trash2 size={14} />
          </button>
        </div>
      </div>
      <div className="p-3 space-y-3">
        {block.type === "text" && (
          <Textarea
            data-testid={`broadcast-block-text-${index}`}
            value={block.content || ""}
            onChange={(e) => onChange({ content: e.target.value })}
            rows={4}
            placeholder="Write a paragraph… blank line = new paragraph."
            className={`${inputCls} h-auto`}
          />
        )}
        {block.type === "cta" && (
          <>
            <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
              <div>
                <div className="text-[10px] uppercase tracking-[0.22em] text-[#A3A3A3] mb-1.5">
                  Button label
                </div>
                <Input
                  data-testid={`broadcast-block-cta-label-${index}`}
                  value={block.label || ""}
                  onChange={(e) => onChange({ label: e.target.value })}
                  placeholder="Download Info Pack"
                  className={inputCls}
                />
              </div>
              <div>
                <div className="text-[10px] uppercase tracking-[0.22em] text-[#A3A3A3] mb-1.5">
                  Button URL
                </div>
                <Input
                  data-testid={`broadcast-block-cta-url-${index}`}
                  value={block.url || ""}
                  onChange={(e) => onChange({ url: e.target.value })}
                  placeholder="https://…"
                  className={inputCls}
                />
              </div>
            </div>
            <div>
              <div className="text-[10px] uppercase tracking-[0.22em] text-[#A3A3A3] mb-1.5">
                Alignment
              </div>
              <select
                data-testid={`broadcast-block-cta-align-${index}`}
                value={block.align || "center"}
                onChange={(e) => onChange({ align: e.target.value })}
                className={`${inputCls} pr-3`}
              >
                <option value="left" className="bg-black">Left</option>
                <option value="center" className="bg-black">Center</option>
                <option value="right" className="bg-black">Right</option>
              </select>
            </div>
          </>
        )}
        {block.type === "image" && (
          <>
            <div>
              <div className="text-[10px] uppercase tracking-[0.22em] text-[#A3A3A3] mb-1.5">
                Image URL
              </div>
              <Input
                data-testid={`broadcast-block-image-url-${index}`}
                value={block.url || ""}
                onChange={(e) => onChange({ url: e.target.value })}
                placeholder="https://…/image.jpg"
                className={inputCls}
              />
            </div>
            <div className="grid grid-cols-1 md:grid-cols-3 gap-3">
              <div>
                <div className="text-[10px] uppercase tracking-[0.22em] text-[#A3A3A3] mb-1.5">
                  Alt text
                </div>
                <Input
                  data-testid={`broadcast-block-image-alt-${index}`}
                  value={block.alt || ""}
                  onChange={(e) => onChange({ alt: e.target.value })}
                  className={inputCls}
                />
              </div>
              <div>
                <div className="text-[10px] uppercase tracking-[0.22em] text-[#A3A3A3] mb-1.5">
                  Alignment
                </div>
                <select
                  data-testid={`broadcast-block-image-align-${index}`}
                  value={block.align || "center"}
                  onChange={(e) => onChange({ align: e.target.value })}
                  className={`${inputCls} pr-3`}
                >
                  <option value="left" className="bg-black">Left</option>
                  <option value="center" className="bg-black">Center</option>
                  <option value="right" className="bg-black">Right</option>
                </select>
              </div>
              <div>
                <div className="text-[10px] uppercase tracking-[0.22em] text-[#A3A3A3] mb-1.5">
                  Width %
                </div>
                <Input
                  data-testid={`broadcast-block-image-width-${index}`}
                  type="number"
                  min={10}
                  max={100}
                  value={block.width_pct ?? 100}
                  onChange={(e) => onChange({ width_pct: Number(e.target.value) || 100 })}
                  className={inputCls}
                />
              </div>
            </div>
          </>
        )}
        {block.type === "divider" && (
          <div className="text-xs text-[#A3A3A3]/70">Horizontal rule — no content.</div>
        )}
        {block.type === "list" && (
          <Textarea
            data-testid={`broadcast-block-list-${index}`}
            value={(block.items || []).join("\n")}
            onChange={(e) =>
              onChange({ items: e.target.value.split("\n") })
            }
            rows={4}
            placeholder="One bullet per line"
            className={`${inputCls} h-auto`}
          />
        )}
        {block.type === "quote" && (
          <>
            <Textarea
              data-testid={`broadcast-block-quote-content-${index}`}
              value={block.content || ""}
              onChange={(e) => onChange({ content: e.target.value })}
              rows={3}
              placeholder="Quote text…"
              className={`${inputCls} h-auto`}
            />
            <Input
              data-testid={`broadcast-block-quote-cite-${index}`}
              value={block.cite || ""}
              onChange={(e) => onChange({ cite: e.target.value })}
              placeholder="Attribution (optional)"
              className={inputCls}
            />
          </>
        )}
      </div>
    </div>
  );
}


// ---- Client-side preview render (mirror of server _render_block_html) ----------

const BADGE_COLORS_CLIENT = {
  purple: { bg: "#D9A6F5", fg: "#0A0A0A" },
  green: { bg: "#B2F57C", fg: "#0A0A0A" },
  red: { bg: "#F57C7C", fg: "#0A0A0A" },
  gold: { bg: "#F5E17C", fg: "#0A0A0A" },
  grey: { bg: "#333333", fg: "#F5F5F0" },
};

const escapeHtml = (s) =>
  String(s ?? "")
    .replace(/&/g, "&amp;")
    .replace(/</g, "&lt;")
    .replace(/>/g, "&gt;")
    .replace(/"/g, "&quot;");

const renderInlineClient = (text) => {
  if (!text) return "";
  let s = escapeHtml(text);
  s = s.replace(/\[\[([^|\]\n]+)(?:\|([a-zA-Z]+))?\]\]/g, (_, label, color) => {
    const c = BADGE_COLORS_CLIENT[(color || "purple").toLowerCase()] || BADGE_COLORS_CLIENT.purple;
    return `<span style="display:inline-block;background:${c.bg};color:${c.fg};padding:2px 8px;font-size:10px;font-weight:900;letter-spacing:1.5px;text-transform:uppercase;margin:0 2px;vertical-align:middle;border-radius:2px;">${escapeHtml(label.trim())}</span>`;
  });
  s = s.replace(/\[([^\]\n]+)\]\(([^)\s]+)\)/g, (_, label, url) => {
    const cleanUrl = url.replace(/&amp;/g, "&");
    return `<a href="${escapeHtml(cleanUrl)}" style="color:#D9A6F5;text-decoration:underline;">${label}</a>`;
  });
  s = s.replace(/\*\*(.+?)\*\*/g, '<strong style="color:#F5F5F0;">$1</strong>');
  s = s.replace(/(^|[^\w])_([^_\n]+)_(?!\w)/g, "$1<em>$2</em>");
  s = s.replace(/\n/g, "<br/>");
  return s;
};

function BlockPreview({ block }) {
  if (block.type === "text") {
    const parts = (block.content || "")
      .replace(/\r\n/g, "\n")
      .split(/\n\n+/)
      .map((p) => p.trim())
      .filter(Boolean);
    return (
      <>
        {parts.map((p, i) => (
          <p
            key={i}
            style={{ color: "#F5F5F0", lineHeight: 1.7, margin: "0 0 18px 0", fontSize: 15 }}
            dangerouslySetInnerHTML={{ __html: renderInlineClient(p) }}
          />
        ))}
      </>
    );
  }
  if (block.type === "cta") {
    if (!block.label || !block.url) return null;
    return (
      <p style={{ margin: "26px 0 10px 0", textAlign: block.align || "center" }}>
        <span
          style={{
            display: "inline-block",
            background: "#D9A6F5",
            color: "#0A0A0A",
            padding: "14px 26px",
            fontWeight: 900,
            fontSize: 12,
            letterSpacing: "2.4px",
            textTransform: "uppercase",
          }}
        >
          {block.label} →
        </span>
      </p>
    );
  }
  if (block.type === "image") {
    if (!block.url) {
      return (
        <p style={{ color: "#A3A3A3", fontStyle: "italic", fontSize: 12, textAlign: "center" }}>
          (image URL not set)
        </p>
      );
    }
    return (
      <p style={{ margin: "18px 0", textAlign: block.align || "center" }}>
        <img
          src={block.url}
          alt={block.alt || ""}
          style={{
            maxWidth: `${block.width_pct || 100}%`,
            height: "auto",
            display: "inline-block",
          }}
        />
      </p>
    );
  }
  if (block.type === "divider") {
    return <hr style={{ border: 0, borderTop: "1px solid #333", margin: "24px 0" }} />;
  }
  if (block.type === "list") {
    const items = (block.items || []).map((i) => (i || "").trim()).filter(Boolean);
    if (items.length === 0) return null;
    return (
      <ul style={{ paddingLeft: 22, margin: "0 0 18px 0" }}>
        {items.map((i, idx) => (
          <li
            key={idx}
            style={{ color: "#F5F5F0", lineHeight: 1.7, margin: "0 0 8px 0", fontSize: 15 }}
            dangerouslySetInnerHTML={{ __html: renderInlineClient(i) }}
          />
        ))}
      </ul>
    );
  }
  if (block.type === "quote") {
    if (!block.content) return null;
    return (
      <div
        style={{
          borderLeft: "3px solid #D9A6F5",
          background: "#0A0A0A",
          padding: "16px 20px",
          margin: "18px 0",
        }}
      >
        <div
          style={{
            color: "#F5F5F0",
            lineHeight: 1.7,
            fontSize: 15,
            fontStyle: "italic",
          }}
          dangerouslySetInnerHTML={{ __html: renderInlineClient(block.content) }}
        />
        {block.cite && (
          <div style={{ color: "#A3A3A3", fontSize: 12, marginTop: 8, fontStyle: "italic" }}>
            — {block.cite}
          </div>
        )}
      </div>
    );
  }
  return null;
}


function UnsubscribesModal({ onClose }) {
  const [records, setRecords] = useState(null);
  const [error, setError] = useState(null);
  const [filter, setFilter] = useState("all"); // "all" | "swingle" | "venues"

  useEffect(() => {
    let alive = true;
    (async () => {
      try {
        const { data } = await adminApi.get("/admin/unsubscribes");
        if (alive) setRecords(data.records || []);
      } catch (err) {
        if (alive) setError(err?.response?.data?.detail || "Failed to load unsubscribes.");
      }
    })();
    return () => {
      alive = false;
    };
  }, []);

  const filtered = (records || []).filter((r) => {
    if (filter === "swingle") return r.venue_id === "swingle";
    if (filter === "venues") return r.venue_id !== "swingle";
    return true;
  });

  const fmtDate = (iso) => {
    if (!iso) return "—";
    try {
      const d = new Date(iso);
      return d.toLocaleString(undefined, {
        year: "numeric",
        month: "short",
        day: "numeric",
        hour: "2-digit",
        minute: "2-digit",
      });
    } catch {
      return iso;
    }
  };

  return (
    <ModalShell onClose={onClose} title="Unsubscribes" testId="unsubs-modal" size="lg">
      <div className="space-y-4" data-testid="unsubs-list">
        <div className="flex items-center gap-2">
          {[
            { id: "all", label: "All" },
            { id: "swingle", label: "Swingle broadcasts" },
            { id: "venues", label: "Venue emails" },
          ].map((tab) => (
            <button
              key={tab.id}
              type="button"
              data-testid={`unsubs-filter-${tab.id}`}
              onClick={() => setFilter(tab.id)}
              className={`px-3 py-1.5 text-[10px] uppercase tracking-[0.22em] font-bold border transition-colors ${
                filter === tab.id
                  ? "border-[#D9A6F5] text-[#D9A6F5]"
                  : "border-white/15 text-[#A3A3A3] hover:text-[#F5F5F0] hover:border-white/30"
              }`}
            >
              {tab.label}
            </button>
          ))}
          <div className="ml-auto text-xs text-[#A3A3A3]">
            {records ? `${filtered.length} record${filtered.length === 1 ? "" : "s"}` : "Loading…"}
          </div>
        </div>

        {error && (
          <div className="text-red-400 text-sm border border-red-400/30 bg-red-400/5 p-3">
            {error}
          </div>
        )}

        {records === null && !error && (
          <div className="py-10 flex items-center justify-center">
            <Loader2 size={20} className="animate-spin text-[#D9A6F5]" />
          </div>
        )}

        {records && filtered.length === 0 && (
          <div
            data-testid="unsubs-empty"
            className="py-12 text-center text-sm text-[#A3A3A3] border border-white/10 bg-black"
          >
            No unsubscribes on record.
          </div>
        )}

        {records && filtered.length > 0 && (
          <div className="border border-white/10 bg-black">
            <div className="grid grid-cols-[1fr_1fr_auto] gap-0 text-[10px] uppercase tracking-[0.22em] text-[#A3A3A3] border-b border-white/10">
              <div className="px-4 py-3">Email</div>
              <div className="px-4 py-3">List</div>
              <div className="px-4 py-3 text-right">Unsubscribed</div>
            </div>
            <div className="max-h-[55vh] overflow-y-auto">
              {filtered.map((r, i) => (
                <div
                  key={`${r.venue_id}-${r.email}-${i}`}
                  data-testid={`unsubs-row-${i}`}
                  className="grid grid-cols-[1fr_1fr_auto] gap-0 border-t border-white/5 hover:bg-white/5"
                >
                  <div className="px-4 py-3 text-sm text-[#F5F5F0] font-mono break-all">
                    {r.email}
                  </div>
                  <div className="px-4 py-3 text-sm text-[#A3A3A3]">
                    {r.venue_id === "swingle" ? (
                      <span className="text-[#D9A6F5]">Swingle outreach</span>
                    ) : (
                      r.venue_name || <span className="text-white/40">{r.venue_id}</span>
                    )}
                  </div>
                  <div className="px-4 py-3 text-xs text-[#A3A3A3] text-right whitespace-nowrap">
                    {fmtDate(r.unsubscribed_at)}
                  </div>
                </div>
              ))}
            </div>
          </div>
        )}

        <p className="text-[10px] text-[#A3A3A3]/60 leading-relaxed">
          Anyone on this list will be skipped automatically on future broadcasts.
          &ldquo;Swingle outreach&rdquo; entries came from opt-outs on Swingle&apos;s own marketing
          emails to venue leads. &ldquo;Venue emails&rdquo; entries opted out of a specific
          venue&apos;s marketing.
        </p>
      </div>
    </ModalShell>
  );
}


function ModalShell({ onClose, title, testId, size = "md", children }) {
  useEffect(() => {
    const onKey = (e) => e.key === "Escape" && onClose();
    document.addEventListener("keydown", onKey);
    return () => document.removeEventListener("keydown", onKey);
  }, [onClose]);

  const maxW = size === "xl" ? "max-w-4xl" : size === "lg" ? "max-w-2xl" : "max-w-lg";

  return (
    <div
      data-testid={testId}
      className="fixed inset-0 z-50 flex items-start md:items-center justify-center px-4 py-8 overflow-y-auto"
    >
      <div
        className="absolute inset-0 bg-black/80 backdrop-blur-sm"
        onClick={onClose}
        aria-hidden
      />
      <div className={`relative bg-[#0A0A0A] border border-white/10 w-full ${maxW}`}>
        <div className="flex items-center justify-between border-b border-white/10 px-6 py-4 sticky top-0 bg-[#0A0A0A] z-10">
          <div className="text-xs uppercase tracking-[0.28em] text-[#D9A6F5] truncate pr-4">{title}</div>
          <button
            type="button"
            onClick={onClose}
            className="text-[#A3A3A3] hover:text-[#F5F5F0] p-1 shrink-0"
            data-testid={`${testId}-close`}
            aria-label="Close"
          >
            <X size={18} />
          </button>
        </div>
        <div className="p-6">{children}</div>
      </div>
    </div>
  );
}

function Field({ label, required, children }) {
  return (
    <div className="space-y-2">
      <div className="text-xs uppercase tracking-[0.22em] text-[#A3A3A3] font-medium">
        {label}
        {required && <span className="text-[#D9A6F5] ml-1">*</span>}
      </div>
      {children}
    </div>
  );
}

const inputCls =
  "bg-black border-white/15 rounded-none h-12 text-[#F5F5F0] placeholder:text-[#A3A3A3]/60 focus-visible:ring-[#D9A6F5] focus-visible:ring-offset-0 w-full px-3";
